import { ReactNode, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Icon,
  IconButton,
  Input,
  Stack,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { LockClosedIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { PlanSection } from '@/lib/types';
import { Field } from '@/components/ui/field';
import { SimpleSelect } from '@/components/ui/simple-select';
import { toaster } from '@/components/ui/toaster';
import useCurriculumsStore from '@/utils/use-curriculums';
import { CurriculumDraft, structureSection } from '@/pages/planner/draft';
import { PlannerAction } from '@/pages/planner/planner-machine';
import CurWizardHeader from './cur-wizard-header';

const DURATION_OPTIONS = [45, 60, 75, 90, 120];

const DURATION_ITEMS = DURATION_OPTIONS.map((minutes) => ({
  label: `${minutes} min`,
  value: String(minutes),
}));

interface CurStructureScreenProps {
  workspaceId: number;
  draft: CurriculumDraft;
  dispatch: (action: PlannerAction) => void;
}

// "curStructure" — step 2 of the create-curriculum wizard. The admin sets the
// plan length and sections once here; creating the curriculum copies that shape
// onto every slot (backend seeds it), so they aren't rebuilding the same
// section list for all N plans. Starts empty — the sections are whatever the
// admin names. Content items are deliberately not editable here — those are
// per plan in the slot builder.
export default function CurStructureScreen({ workspaceId, draft, dispatch }: CurStructureScreenProps) {
  const create = useCurriculumsStore((s) => s.create);
  const [structure, setStructure] = useState<CurriculumDraft>(draft);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const updateSection = (uid: string, changes: Partial<PlanSection>) => {
    setStructure((prev) => ({
      ...prev,
      sections: prev.sections.map((section) =>
        section.uid === uid ? { ...section, ...changes } : section,
      ),
    }));
  };

  const removeSection = (uid: string) => {
    setStructure((prev) => ({
      ...prev,
      sections: prev.sections.filter((section) => section.uid !== uid),
    }));
  };

  const addSection = () => {
    setStructure((prev) => ({ ...prev, sections: [...prev.sections, structureSection()] }));
  };

  const sectionTotal = structure.sections.reduce(
    (total, section) => total + (section.duration_minutes || 0),
    0,
  );

  const handleCreate = async () => {
    setIsSubmitting(true);
    try {
      const created = await create({
        workspace_id: workspaceId,
        name: structure.name,
        description: structure.description,
        // Curriculum-wide, typed on step 1 — carried through the draft.
        learning_outcomes: structure.learning_outcomes,
        prerequisites: structure.prerequisites,
        plan_count: structure.plan_count,
        duration_minutes: structure.duration_minutes,
        // Sections are named free-text; drop blank rows rather than seeding
        // every plan with an unnamed section the admin has to clean up.
        sections: structure.sections
          .filter((section) => section.name.trim() !== '')
          .map((section) => ({ ...section, name: section.name.trim() })),
      });
      toaster.create({ type: 'success', title: 'Curriculum created' });
      dispatch({ type: 'CURRICULUM_CREATED', curriculumId: created.id });
    } catch (error: any) {
      toaster.create({
        type: 'error',
        title: error.response?.data?.error ?? 'Failed to create curriculum',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Stack gap={0}>
      <CurWizardHeader
        step={2}
        title={structure.name.trim() || 'Untitled curriculum'}
        actions={
          <>
            <Button
              variant='outline'
              size='sm'
              onClick={() => dispatch({ type: 'CURRICULUM_STRUCTURE_BACK', draft: structure })}
            >
              Back
            </Button>
            <Button colorPalette='brand' size='sm' loading={isSubmitting} onClick={handleCreate}>
              Create &amp; build plans
            </Button>
          </>
        }
      />

      <Flex align='center' gap={2} bg='slot3.bg' px={4} py={2}>
        <Box w='7px' h='7px' borderRadius='full' bg='slot3.fg' flexShrink={0} />
        <Text fontSize='xs' color='slot3.fg' fontWeight='500'>
          This structure is applied to all {structure.plan_count} plans — set the length and sections
          once, then fill in content plan by plan.
        </Text>
      </Flex>

      <Flex align='stretch' direction={{ base: 'column', md: 'row' }}>
        {/* 1 — Plan defaults */}
        <Stack
          gap={0}
          w={{ base: 'full', md: '300px' }}
          flexShrink={0}
          borderRightWidth={{ base: 0, md: '1px' }}
          borderBottomWidth={{ base: '1px', md: 0 }}
          borderColor='border'
        >
          <Flex align='center' gap={2} px={4} py={3} bg='bg.subtle' borderBottomWidth='1px' borderColor='border'>
            <StepBadge>1</StepBadge>
            <Text fontSize='sm' fontWeight='600' color='fg.strong'>
              Plan defaults
            </Text>
          </Flex>

          <Stack gap={4} p={4}>
            <Field label='Plan length'>
              <SimpleSelect
                items={DURATION_ITEMS}
                value={String(structure.duration_minutes)}
                onValueChange={(value) =>
                  setStructure((prev) => ({ ...prev, duration_minutes: Number(value) }))
                }
              />
            </Field>

            <Box>
              <Text textStyle='eyebrow' mb={1}>
                Plans in this curriculum
              </Text>
              <Text fontFamily='heading' fontSize='h3' fontWeight='800' color='fg.strong'>
                {structure.plan_count}
              </Text>
            </Box>

            <Box bg='bg.subtle' borderWidth='1px' borderColor='border' borderRadius='md' p={3}>
              <Text fontSize='xs' color='fg.muted' lineHeight='relaxed'>
                Every plan in this curriculum starts from this structure. You add the content items
                plan by plan in the next step.
              </Text>
            </Box>
          </Stack>
        </Stack>

        {/* 2 — Sections */}
        <Stack gap={0} flex='1' minW={0}>
          <Flex align='center' gap={2} px={4} py={3} borderBottomWidth='1px' borderColor='border'>
            <StepBadge>2</StepBadge>
            <Text fontSize='sm' fontWeight='600' color='fg.strong'>
              Sections
            </Text>
            <Text fontSize='xs' color='fg.muted' ml='auto'>
              {sectionTotal} min across {structure.sections.length}{' '}
              {structure.sections.length === 1 ? 'section' : 'sections'} · plan length{' '}
              {structure.duration_minutes} min
            </Text>
          </Flex>

          <Stack gap={3} p={4}>
            {structure.sections.length === 0 && (
              <Flex
                align='center'
                justify='center'
                px={3}
                py={6}
                borderWidth='1px'
                borderStyle='dashed'
                borderColor='border'
                borderRadius='md'
              >
                <Text fontSize='xs' color='fg.subtle' textAlign='center'>
                  No sections yet — add the ones every plan in this curriculum should start with.
                </Text>
              </Flex>
            )}

            {structure.sections.map((section, index) => (
              <Box key={section.uid} borderWidth='1px' borderColor='border' borderRadius='md'>
                <Flex
                  align='center'
                  gap={2}
                  px={3}
                  py={2}
                  bg='bg.subtle'
                  borderBottomWidth='1px'
                  borderColor='border'
                  borderTopRadius='md'
                >
                  <Text
                    textStyle='eyebrow'
                    bg='bg'
                    px={2}
                    py={0.5}
                    borderRadius='sm'
                    flexShrink={0}
                  >
                    {index + 1}
                  </Text>
                  <Input
                    value={section.name}
                    onChange={(event) => updateSection(section.uid, { name: event.target.value })}
                    placeholder='Section name…'
                    size='sm'
                    fontWeight='600'
                    flex='1'
                    minW={0}
                  />
                  <Flex align='center' gap={1} flexShrink={0}>
                    {/* 0 is "unset": show a blank input so a new row reads as
                        empty rather than pre-filled with a duration the admin
                        never chose. Clearing the field parses back to 0. */}
                    <Input
                      type='number'
                      min={0}
                      value={section.duration_minutes || ''}
                      onChange={(event) =>
                        updateSection(section.uid, {
                          duration_minutes: parseInt(event.target.value, 10) || 0,
                        })
                      }
                      placeholder='—'
                      size='sm'
                      width='16'
                      textAlign='center'
                    />
                    <Text fontSize='xs' color='fg.muted'>
                      min
                    </Text>
                  </Flex>
                  <IconButton
                    aria-label={`Remove ${section.name.trim() || `section ${index + 1}`}`}
                    variant='ghost'
                    size='xs'
                    onClick={() => removeSection(section.uid)}
                    _hover={{ color: 'danger', bg: 'danger.subtle' }}
                  >
                    <Icon as={XMarkIcon} w={4} h={4} />
                  </IconButton>
                </Flex>

                <Stack gap={2} p={3}>
                  <Textarea
                    value={section.notes}
                    onChange={(event) => updateSection(section.uid, { notes: event.target.value })}
                    placeholder='Section notes carried into every plan (optional)…'
                    size='sm'
                    rows={2}
                  />
                  <Flex
                    align='center'
                    gap={2}
                    px={3}
                    py={2}
                    borderWidth='1px'
                    borderStyle='dashed'
                    borderColor='border'
                    borderRadius='sm'
                    color='fg.subtle'
                  >
                    <Icon as={LockClosedIcon} w={3.5} h={3.5} />
                    <Text fontSize='xs'>Content items are added per plan</Text>
                  </Flex>
                </Stack>
              </Box>
            ))}

            <Button
              variant='outline'
              borderStyle='dashed'
              borderColor='border.strong'
              color='fg.muted'
              onClick={addSection}
            >
              + Add section
            </Button>
          </Stack>
        </Stack>
      </Flex>
    </Stack>
  );
}

function StepBadge({ children }: { children: ReactNode }) {
  return (
    <Flex
      align='center'
      justify='center'
      w='22px'
      h='22px'
      borderRadius='full'
      bg='brand.solid'
      color='brand.contrast'
      fontFamily='heading'
      fontSize='2xs'
      fontWeight='800'
      flexShrink={0}
    >
      {children}
    </Flex>
  );
}
